import React, { useEffect, useMemo, useState } from 'react';
import type { RequiredAlertsListType } from '@nxsys/store/slices/dashboard/types';
import AppCard from '@nxsys/components/AppCard';
import RequiredAlertsListModal from './RequiredAlertsListModal';
import AppIconCard from '@nxsys/components/AppIconCard';
import {
  PAYMENT_MODEL_ENUM,
  getIrelandPaymentModels,
} from '@nxsys/constants/AppConstants';
import { Icons } from '@nxsys/types/icons';
import { getDashboardRequiredAlerts } from '@nxsys/store/slices/dashboard/actions';
import { AppDispatch, AppState } from '@nxsys/store';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  getRequiredAlertsOrder,
  ALERT_LABEL_KEYS,
  CRITICAL_ALERT_KEYS,
  getIconForAlert,
} from './actionRequiredHelpers';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useCanAccessEmployees } from '@nxsys/hooks/useCanAccessEmployees';
import { IRELAND } from '@nxsys/store/constants/keys';

export type AlertItem = {
  id: string;
  count: number;
  label: string;
  isCritical: boolean;
  icon: React.ReactNode;
};

const alertIdToListType = (id: string): RequiredAlertsListType | null => {
  const map: Record<string, RequiredAlertsListType> = {
    missingNINumber: 'missingNINumber',
    missingPPSNumber: 'missingPPSNumber',
    missingAddress: 'missingAddress',
    missingEmail: 'missingEmail',
    onHold: 'onHold',
    withRecurringPayments: 'withRecurringPayments',
    withRecurringDeductions: 'withRecurringDeductions',
    missingRPNActionNeeded: 'missingRPNActionNeeded',
    missingRPNAwaiting: 'missingRPNAwaiting',
  };
  return map[id] ?? null;
};

const ActionRequiredSectionView: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<AlertItem | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { requiredAlertsData } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  // Country-only: this widget is reachable from DashboardUmbrella's admin
  // Timesheets section too, so `isBureau &&` hid the PPSN alert labels and
  // the CIS/PAYEE filter for an umbrella-Ireland tenant.
  const isIreland = payrollCountry === IRELAND;
  // The alert popups drill into employee records, so a dashboard-only user
  // (no COMPANY.EMPLOYEES.LIST) may see the counts but cannot open the list.
  const canAccessEmployees = useCanAccessEmployees();
  const validPaymentModels = isIreland
    ? getIrelandPaymentModels(isBureau)
    : isBureau
      ? [PAYMENT_MODEL_ENUM.PAYEE]
      : undefined;
  const { messages } = useTypedIntl();

  useEffect(() => {
    dispatch(getDashboardRequiredAlerts(
      validPaymentModels ? { paymentModels: validPaymentModels } : undefined,
    ));
  }, []);

  // NX-5676: Confirm Payment (and the draft-payroll confirm) are modals on this
  // same /dashboards route, so this section never remounts when a payroll is
  // confirmed — its counts would stay stale until a full reload. Refetch on the
  // shared dashboard reload event, the same pattern the other sections use.
  useEffect(() => {
    const handleReload = () =>
      dispatch(
        getDashboardRequiredAlerts(
          validPaymentModels
            ? { paymentModels: validPaymentModels }
            : undefined,
        ),
      );
    window.addEventListener('dashboard:reload-actionRequired', handleReload);
    return () =>
      window.removeEventListener(
        'dashboard:reload-actionRequired',
        handleReload,
      );
  }, []);

  const alerts: AlertItem[] = useMemo(() => {
    const alertsData = requiredAlertsData.data;
    if (!alertsData) return [];
    return getRequiredAlertsOrder(isIreland).map((id) => {
      const count = alertsData[id] ?? 0;
      const isCritical = CRITICAL_ALERT_KEYS.has(id);
      const label = messages[ALERT_LABEL_KEYS[id]] || id;
      return {
        id,
        count,
        label,
        isCritical,
        icon: getIconForAlert(id, isCritical),
      };
    });
  }, [requiredAlertsData.data, messages, isIreland]);

  const handleAlertClick = (alert: AlertItem) => {
    if (!canAccessEmployees) return;
    const listType = alertIdToListType(alert.id);
    if (listType) {
      setSelectedAlert(alert);
      setModalOpen(true);
    }
  };

  const listType = selectedAlert ? alertIdToListType(selectedAlert.id) : null;

  return (
    <>
      <AppCard className="p-[24px]" loading={requiredAlertsData.loading}>
        <AppIconCard
          iconName="IconClipboardMinus"
          title={messages['common.actionRequired'] || 'Action Required/ Alerts'}
        />
        <div className="mt-[18px]">
          <div className="flex flex-col gap-[16px]">
            {alerts.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                No data to show
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  onClick={() => handleAlertClick(alert)}
                  className={`flex items-center justify-between py-2 px-4 rounded-lg ${
                    canAccessEmployees ? 'dashboard-hover-card' : ''
                  } ${alert.isCritical ? 'bg-red-50' : 'bg-[#F3F4F8]'}`}
                >
                  <div className="flex items-center gap-x-[8px] flex-1 min-w-0">
                    <div className="flex items-center gap-x-[8px]">
                      <div className="flex-shrink-0">{alert.icon}</div>
                      <span
                        className={`text-[22px] font-bold ${
                          alert.isCritical ? 'text-red-600' : 'text-neutral-700'
                        }`}
                      >
                        {alert.count}
                      </span>
                    </div>
                    <span className="text-[14px] text-neutral-500 truncate">
                      {alert.label}
                    </span>
                  </div>
                  {canAccessEmployees && <Icons.IconArrowRight />}
                </div>
              ))
            )}
          </div>
        </div>
      </AppCard>

      {listType && selectedAlert && (
        <RequiredAlertsListModal
          type={listType}
          title={selectedAlert.label}
          paymentModels={validPaymentModels}
          isOpen={modalOpen}
          toggle={() => {
            setModalOpen(false);
            setSelectedAlert(null);
          }}
          onChanged={() =>
            dispatch(
              getDashboardRequiredAlerts(
                validPaymentModels
                  ? { paymentModels: validPaymentModels }
                  : undefined,
              ),
            )
          }
        />
      )}
    </>
  );
};

export default ActionRequiredSectionView;
